import {
  Button,
  Chip,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Text,
  Title,
} from "@assetwatch/ui";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useGetCustomerContext } from "../../queries/useGetCustomerContext";
import { useGetEnterpriseOverview } from "../../queries/useGetEnterpriseOverview";
import { useHealthScoreQuery } from "../../queries/useGetHealthScore";
import {
  getFacilityDisplayName,
  getFacilityScore,
  getFacilityStatus,
} from "../EnterpriseOverviewLanding/utils";
import { ScoreBreakdownTab } from "./ScoreBreakdownTab";
import {
  buildBreakdownRows,
  getFacilityRegionLabel,
  statusToneMap,
} from "./utils";

const surfaceCloseTransitionMs = 280;
const classicFrontendBaseUrl =
  import.meta.env.VITE_FRONTEND_BASE_URL ?? "http://localhost:5173";

function getClassicFacilityUrl(externalCustomerId: string, facilityId: number) {
  const url = new URL(
    `/customers/${encodeURIComponent(externalCustomerId)}`,
    classicFrontendBaseUrl,
  );
  url.searchParams.set("fid", String(facilityId));
  return url.toString();
}

export function FacilityDetailDrawerContent({
  facilityId,
  externalCustomerId,
  onClose,
}: {
  facilityId: number;
  externalCustomerId: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [isSurfaceOpen, setIsSurfaceOpen] = useState(true);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { data: customer } = useGetCustomerContext(externalCustomerId);
  const { data: overview } = useGetEnterpriseOverview(customer.customerId);
  const { data: health } = useHealthScoreQuery(customer.customerId);
  const overviewFacility = overview.facilities.find(
    (row) => row.facilityId === facilityId,
  );
  const healthFacility = health?.facilities.find(
    (row) => row.facilityId === facilityId,
  );

  function clearPendingClose() {
    if (closeTimeoutRef.current === null) return;
    clearTimeout(closeTimeoutRef.current);
    closeTimeoutRef.current = null;
  }

  function closeAfterAnimation(afterClose: () => void = onClose) {
    clearPendingClose();
    setIsSurfaceOpen(false);
    closeTimeoutRef.current = setTimeout(() => {
      closeTimeoutRef.current = null;
      afterClose();
    }, surfaceCloseTransitionMs);
  }

  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current === null) return;
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    };
  }, []);

  if (!overviewFacility) {
    return (
      <Drawer
        open={isSurfaceOpen}
        onOpenChange={(open) => {
          if (!open) closeAfterAnimation();
        }}
      >
        <DrawerContent data-testid="facility-maturity-drawer">
          <DrawerHeader
            title={t("enterprise.facilityDetail.drawerFacilityFallback")}
            subtitle={t("enterprise.facilityDetail.notFoundTitle")}
            closeLabel={t("enterprise.facilityDetail.drawerCloseAriaLabel")}
          />
          <DrawerBody>
            <div className="flex flex-1 flex-col justify-center gap-(--spacing-12)">
              <Title size="lg">
                {t("enterprise.facilityDetail.notFoundTitle")}
              </Title>
              <Text tone="subtle">
                {t("enterprise.facilityDetail.notFoundDescription")}
              </Text>
            </div>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    );
  }

  const overallScore = getFacilityScore(overviewFacility);
  const status = getFacilityStatus(overviewFacility);
  const breakdownRows = buildBreakdownRows({
    facility: overviewFacility,
    alertOutcomeScore: healthFacility?.alertOutcomes ?? null,
    executionSpeedScore: healthFacility?.executionSpeed ?? null,
    t,
  });

  const classicFacilityUrl = getClassicFacilityUrl(
    externalCustomerId,
    facilityId,
  );
  const viewFacilityLabel = t(
    "enterprise.facilityDetail.drawer.viewFacilityButton",
  );

  return (
    <Drawer
      open={isSurfaceOpen}
      onOpenChange={(open) => {
        if (!open) closeAfterAnimation();
      }}
    >
      <DrawerContent data-testid="facility-maturity-drawer">
        <DrawerHeader
          title={getFacilityDisplayName(overviewFacility)}
          subtitle={getFacilityRegionLabel(overviewFacility)}
          accessory={
            <Chip
              tone={statusToneMap[status]}
              label={t(`enterprise.overview.statusChip.${status}`)}
            />
          }
          closeLabel={t("enterprise.facilityDetail.drawerCloseAriaLabel")}
        />
        <DrawerBody gap="none">
          <Tabs value="score-breakdown">
            <TabsList>
              <TabsTrigger value="score-breakdown">
                {t("enterprise.facilityDetail.drawer.scoreBreakdownTab")}
              </TabsTrigger>
            </TabsList>
            <TabsContent value="score-breakdown">
              <div className="pt-(--spacing-20)">
                <ScoreBreakdownTab
                  rows={breakdownRows}
                  overallScore={overallScore}
                  status={status}
                />
              </div>
            </TabsContent>
          </Tabs>
        </DrawerBody>
        <DrawerFooter>
          <div className="flex w-full justify-start">
            <Button
              render={
                // biome-ignore lint/a11y/useAnchorContent: Base UI render prop receives the Button children as anchor content at runtime.
                <a
                  href={classicFacilityUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={viewFacilityLabel}
                />
              }
              variant="outline"
              icon="arrow-up-right"
            >
              {viewFacilityLabel}
            </Button>
          </div>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}
